import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, Loader2, ReceiptText } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";

import type { EvidenceTarget } from "./types";

export function EvidenceModal({
	target,
	onClose,
}: {
	target: EvidenceTarget;
	onClose: () => void;
}) {
	if (target.kind === "receipt") {
		return <ReceiptEvidence id={target.id} onClose={onClose} />;
	}

	return <BillEvidence id={target.id} onClose={onClose} />;
}

function EvidenceShell({
	title,
	description,
	isLoading,
	isError,
	empty,
	onClose,
	children,
}: {
	title: string;
	description: string;
	isLoading: boolean;
	isError: boolean;
	empty: boolean;
	onClose: () => void;
	children: ReactNode;
}) {
	return (
		<OverlayShell label={title} onClose={onClose} className="items-center justify-center p-4">
			<div className="overlay-panel max-w-lg overflow-hidden">
				<div className="border-b border-border p-5">
					<h2 className="type-section-title text-foreground">{title}</h2>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
				<div className="min-h-40 overflow-y-auto p-3">
					{isLoading && (
						<div className="flex h-40 items-center justify-center text-muted-foreground">
							<Loader2 className="animate-spin" />
						</div>
					)}
					{isError && (
						<EmptyState icon={Landmark} title="Não foi possível buscar os comprovantes" />
					)}
					{!isLoading && !isError && empty && (
						<EmptyState
							icon={ReceiptText}
							title="Nenhum comprovante compatível"
							description="Atualize os dados e tente novamente."
						/>
					)}
					{!isLoading && !isError && children}
				</div>
				<div className="flex justify-end border-t border-border p-3">
					<Button variant="ghost" onClick={onClose}>
						Fechar
					</Button>
				</div>
			</div>
		</OverlayShell>
	);
}

function Candidate({
	title,
	detail,
	amountCents,
	isPending,
	onChoose,
}: {
	title: string;
	detail: string;
	amountCents: number;
	isPending: boolean;
	onChoose: () => void;
}) {
	return (
		<div className="flex items-center gap-3 rounded-lg px-2 py-3 hover:bg-secondary/50">
			<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
				<Landmark className="size-4" />
			</div>
			<div className="min-w-0 flex-1">
				<p className="truncate text-xs font-semibold text-foreground">{title}</p>
				<p className="text-xs text-muted-foreground">{detail}</p>
			</div>
			<p className="font-mono text-xs font-semibold text-foreground">
				{FinanceiroLabels.money(amountCents)}
			</p>
			<Button size="sm" variant="outline" disabled={isPending} onClick={onChoose}>
				Vincular
			</Button>
		</div>
	);
}

function useLinked(onClose: () => void) {
	const queryClient = useQueryClient();

	return async () => {
		await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
		toast.success("Comprovante vinculado");
		onClose();
	};
}

function dateLabel(value: string | null | undefined) {
	return typeof value === "string"
		? (format.date(value) ?? "Data não informada")
		: "Data não informada";
}

function ReceiptEvidence({ id, onClose }: { id: number; onClose: () => void }) {
	const linked = useLinked(onClose);
	const candidates = useQuery(
		orpc.financeiro.receipts.evidenceCandidates.queryOptions({ input: { id } }),
	);
	const linkBank = useMutation(
		orpc.financeiro.receipts.linkBank.mutationOptions({
			onSuccess: linked,
			onError: () => toast.error("Esse comprovante não está mais disponível"),
		}),
	);
	const linkCard = useMutation(
		orpc.financeiro.receipts.linkCardSale.mutationOptions({
			onSuccess: linked,
			onError: () => toast.error("Essa venda não está mais disponível"),
		}),
	);
	const rows = candidates.data?.data ?? [];

	return (
		<EvidenceShell
			title="Vincular comprovante"
			description="Escolha o movimento que confirma este recebimento."
			isLoading={candidates.isLoading}
			isError={candidates.isError}
			empty={rows.length === 0}
			onClose={onClose}
		>
			{candidates.data?.kind === "bank" &&
				candidates.data.data.map((bank) => (
					<Candidate
						key={bank.id}
						title={bank.description ?? "Entrada bancária"}
						detail={dateLabel(bank.effective_on)}
						amountCents={bank.amount_cents}
						isPending={linkBank.isPending}
						onChoose={() => linkBank.mutate({ id, bank_transaction_id: bank.id })}
					/>
				))}
			{candidates.data?.kind === "card" &&
				candidates.data.data.map((sale) => (
					<Candidate
						key={sale.id}
						title={sale.product}
						detail={`${format.date(sale.sale_on)}, venda no cartão`}
						amountCents={sale.gross_cents}
						isPending={linkCard.isPending}
						onChoose={() => linkCard.mutate({ id, card_sale_id: sale.id })}
					/>
				))}
		</EvidenceShell>
	);
}

function BillEvidence({ id, onClose }: { id: number; onClose: () => void }) {
	const linked = useLinked(onClose);
	const candidates = useQuery(orpc.financeiro.bills.bankCandidates.queryOptions({ input: { id } }));
	const link = useMutation(
		orpc.financeiro.bills.linkBank.mutationOptions({
			onSuccess: linked,
			onError: () => toast.error("Esse comprovante não está mais disponível"),
		}),
	);

	return (
		<EvidenceShell
			title="Vincular pagamento"
			description="Escolha a saída bancária que confirma o pagamento desta conta."
			isLoading={candidates.isLoading}
			isError={candidates.isError}
			empty={(candidates.data?.length ?? 0) === 0}
			onClose={onClose}
		>
			{candidates.data?.map((bank) => (
				<Candidate
					key={bank.id}
					title={bank.description ?? "Saída bancária"}
					detail={dateLabel(bank.effective_on)}
					amountCents={bank.amount_cents}
					isPending={link.isPending}
					onChoose={() => link.mutate({ id, bank_transaction_id: bank.id })}
				/>
			))}
		</EvidenceShell>
	);
}
